import classNames from 'classnames';
import * as React from 'react';

import { makeGridPopupContainer } from '../../containers/GridPopup/GridPopup';
import { mapBannerToPcodeRenderData } from '../../mappers/banner/banner';
import {
    isCpcVideoBannerCheck,
    isPcodeBannerCheck,
    isTextBannerCheck
} from '../../service/BannerHelpers/BannerHelpers';
import { Bem } from '../../service/Bem/Bem';
import { isInternalBannerCheck } from '../../service/InternalBannerHelpers/InternalBannerHelpers';
import { TestBem } from '../../service/TestBem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { banners } from '../../typings/common';
import {
    AdvType,
    AdvTypeClient,
    BannerImage,
    BannerPreviewData,
    BannerVideo
} from '../../typings/domain';
import { BannerViewType, PcodeRenderData } from '../../typings/pcode';
import {
    GridRecommendationsData,
    RecommendationType
} from '../../typings/recommendations';
import { BannerPcodePreviewCell } from '../BannerPcodePreviewCell/BannerPcodePreviewCell';
import { GridBannerImageAdvCell } from '../GridBannerImageAdvCell/GridBannerImageAdvCell';
import { GridBannerInternalAdvCell } from '../GridBannerInternalAdvCell/GridBannerInternalAdvCell';
import { GridBannerTextAdvCell } from '../GridBannerTextAdvCell/GridBannerTextAdvCell';
import { GridBannerVideoAdvCell } from '../GridBannerVideoAdvCell/GridBannerVideoAdvCell';
import { GridCellContentOverflow } from '../GridCellContentOverflow/GridCellContentOverflow';
import { RecommendationCardView } from '../RecommendationCard/RecommendationCard';
import {
    RecommendationsTooltip,
    RecommendationsTooltipProps
} from '../RecommendationsTooltip/RecommendationsTooltip';

import './BannerPreview.css';

interface BannerPreviewProps {
    bannerPreviewData: BannerPreviewData;
    canvasDomain?: string;
    clientId?: string;
    recommendations?: {
        items: GridRecommendationsData;
        onOpenRecommendationsPopup?: () => void;
        onSendLicenseButtonClick?: () => void;
        onShowRecommendation?: (
            view: RecommendationCardView,
            type: RecommendationType
        ) => void;
    };
    showBannersPreview?: (
        data: PcodeRenderData,
        livePreviewUrl?: string
    ) => void;
    showBannerCreativePreview?: (
        data: PcodeRenderData,
        livePreviewUrl: string
    ) => void;
    column?: boolean;
}

const GridPopupContainer = makeGridPopupContainer<RecommendationsTooltipProps>(
    RecommendationsTooltip
);

const BLOCK = 'banner-preview';

export class BannerPreview extends React.PureComponent<BannerPreviewProps> {
    render() {
        const {
            bannerPreviewData: { id }
        } = this.props;

        return (
            <div
                className={classNames(
                    Bem.cls(BLOCK),
                    TestBem.cls({
                        block: BLOCK,
                        mods: {
                            id
                        }
                    })
                )}
            >
                {this.renderGridCellContentOverflow()}
                {this.renderRecommendations()}
            </div>
        );
    }

    private renderGridCellContentOverflow() {
        const { recommendations } = this.props;

        if (!recommendations) {
            return this.renderPreviewCell();
        }

        return (
            <GridCellContentOverflow>
                {this.renderPreviewCell()}
            </GridCellContentOverflow>
        );
    }

    private renderPreviewCell() {
        const {
            bannerPreviewData: {
                id,
                title = '',
                titleExtension,
                body = '',
                domain,
                href,
                bannerType,
                image,
                video,
                description = '',
                livePreviewUrl
            },
            showBannersPreview,
            showBannerCreativePreview,
            column
        } = this.props;
        const { bannerPreviewData } = this.props;

        const isPcodeBanner = isPcodeBannerCheck(bannerPreviewData);
        const isTextBanner = isTextBannerCheck(bannerType);
        const isCpcVideoBanner = isCpcVideoBannerCheck(bannerType);
        const isInternalBanner = isInternalBannerCheck(bannerType);
        const needShowCreativePreview = Boolean(
            livePreviewUrl && showBannerCreativePreview
        );
        const needShowPreview = Boolean(showBannersPreview);

        if (isCpcVideoBanner) {
            if (!video) {
                throw new Error(`Видео баннер ${id} без креатива`);
            }
        } else if (!isTextBanner && !isInternalBanner) {
            if (!image) {
                throw new Error(`Графический баннер ${id} без изображения`);
            }
        }

        let previewCell = null;

        if (isPcodeBanner) {
            const pcodeData = mapBannerToPcodeRenderData(
                bannerPreviewData,
                false,
                BannerViewType.adaptive0418
            );

            previewCell = (
                <BannerPcodePreviewCell
                    data={pcodeData}
                    id={id}
                    onClick={
                        needShowPreview ? this.showBannerPreview : undefined
                    }
                />
            );
        } else if (isTextBanner) {
            previewCell = (
                <GridBannerTextAdvCell
                    id={id}
                    column={column}
                    title={title}
                    titleExtension={titleExtension}
                    body={body}
                    domain={domain}
                    href={href}
                    image={image}
                    onClick={
                        needShowPreview ? this.showBannerPreview : undefined
                    }
                />
            );
        } else if (isCpcVideoBanner) {
            previewCell = (
                <GridBannerVideoAdvCell
                    id={id}
                    video={video as BannerVideo} // не undefined из-за проверки выше
                    onShowPreview={
                        needShowCreativePreview
                            ? this.showBannerCreativePreview
                            : undefined
                    }
                />
            );
        } else if (isInternalBanner) {
            previewCell = (
                <GridBannerInternalAdvCell id={id} title={description} />
            );
        } else {
            previewCell = (
                <GridBannerImageAdvCell
                    id={id}
                    image={image as BannerImage} // не undefined из-за проверки выше
                    isVideo={Array<AdvType | AdvTypeClient>(
                        AdvType.CPM_VIDEO,
                        AdvType.CPM_INDOOR,
                        AdvType.CPM_OUTDOOR,
                        AdvType.CPM_AUDIO
                    ).includes(bannerType)}
                    bannerType={bannerType}
                    onShowPreview={
                        needShowCreativePreview
                            ? this.showBannerCreativePreview
                            : undefined
                    }
                />
            );
        }

        return (
            <div className={Bem.cls(BLOCK, 'preview-cell')}>{previewCell}</div>
        );
    }

    private showBannerPreview = (): void => {
        const {
            bannerPreviewData,
            showBannersPreview,
            canvasDomain,
            clientId
        } = this.props;
        let { livePreviewUrl } = bannerPreviewData;

        if (!livePreviewUrl) {
            const isDefault = Boolean(
                bannerPreviewData.image && bannerPreviewData.image.url.length
            );

            livePreviewUrl = isDefault
                ? UrlHelpers.FormatUrl({
                      host: canvasDomain,
                      path: 'video-additions/video-motion/preview',
                      query: { compact: 1, client_id: clientId }
                  })
                : undefined;
        }

        if (showBannersPreview) {
            const pcodeData = mapBannerToPcodeRenderData(bannerPreviewData);

            showBannersPreview(pcodeData, livePreviewUrl);
        }
    };

    private showBannerCreativePreview = (): void => {
        const { bannerPreviewData, showBannerCreativePreview } = this.props;

        if (bannerPreviewData.livePreviewUrl && showBannerCreativePreview) {
            const pcodeData = mapBannerToPcodeRenderData(
                bannerPreviewData,
                true
            );

            showBannerCreativePreview(
                pcodeData,
                bannerPreviewData.livePreviewUrl
            );
        }
    };

    private renderRecommendations() {
        const { recommendations } = this.props;

        if (!recommendations) {
            return null;
        }

        return (
            <GridPopupContainer
                cls={Bem.cls('grid-banner-image-adv-cell', 'recommendations')}
                view={banners}
                recommendations={recommendations.items.data}
                onPopupOpen={recommendations.onOpenRecommendationsPopup}
                recommendationIconType={
                    recommendations.items.recommendationsIconType
                }
                onShowRecommendation={recommendations.onShowRecommendation}
                onSendLicenseButtonClick={
                    recommendations.onSendLicenseButtonClick
                }
            />
        );
    }
}
